import {
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import {
    getYouTubeVideoKey,
    isNullOrWhiteSpace
} from '@classifieds-sell/shared';
import { MotorsDraftFacade } from '@classifieds-sell/state';
import { Subject } from 'rxjs';
import { MotorsSectionBase } from '../motors-section-base';

@Component({
    selector: 'motors-description-and-photos-video-section',
    templateUrl: './motors-description-and-photos-video-section.component.html',
    styleUrls: ['./motors-description-and-photos-video-section.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class MotorsDescriptionAndPhotosVideoSectionComponent
    extends MotorsSectionBase
    implements OnInit, OnDestroy
{
    private _destroyed$: Subject<void> = new Subject<void>();

    @ViewChild('motorsDescriptionAndPhotosVideoSectionForm')
    motorsDescriptionAndPhotosVideoSectionForm: NgForm;

    addVideoText = 'Add video';
    removeVideoText = 'Remove video';

    inputDisabled = false;
    buttonTitle = this.addVideoText;

    isProcessing = false;
    hasError = false;

    private _youTubeUrl = 'https://www.youtube.com/embed/';
    safeYouTubeVideoUrl: SafeResourceUrl;
    originalYouTubeVideoKey: string;
    previewYouTubeVideoUrl = false;

    constructor(
        draftFacade: MotorsDraftFacade,
        private _changeDetectorRef: ChangeDetectorRef,
        private _sanitizer: DomSanitizer
    ) {
        super(draftFacade);
    }

    ngOnDestroy(): void {
        super.onDestroy();
        this._destroyed$.next();
    }

    ngOnInit(): void {
        super.onInit(() => {
            const youTubeVideoKey =
                this.draftData?.descriptionAndPhotos?.youTubeVideoKey;
            const youTubeIdPattern = /^[a-zA-Z0-9_-]{11}$/;
            if (
                (youTubeVideoKey || '').length > 0 &&
                youTubeIdPattern.test(youTubeVideoKey)
            ) {
                const safeYouTubeVideoUrl =
                    this._sanitizer.bypassSecurityTrustResourceUrl(
                        `${this._youTubeUrl}${youTubeVideoKey}`
                    );
                if (this.originalYouTubeVideoKey !== youTubeVideoKey) {
                    this.safeYouTubeVideoUrl = safeYouTubeVideoUrl;
                    this.originalYouTubeVideoKey = youTubeVideoKey;
                    if (!this.previewYouTubeVideoUrl) {
                        this.previewYouTubeVideoUrl = true;
                    }
                }
                this.inputDisabled = true;
                this.buttonTitle = this.removeVideoText;
            } else {
                this.inputDisabled = false;
                this.buttonTitle = this.addVideoText;
            }
            this._changeDetectorRef.markForCheck();
        });
    }

    isValid(): boolean {
        return true; // allow user to progress through form even if the video link is broken
    }

    dirty(): boolean {
        if (this.motorsDescriptionAndPhotosVideoSectionForm.form.disabled) {
            return this.motorsDescriptionAndPhotosVideoSectionForm.form.controls
                .youtubeVideo.dirty;
        }
        return this.motorsDescriptionAndPhotosVideoSectionForm.form.dirty;
    }

    validateVideo(): void {
        const videoKey = getYouTubeVideoKey(
            this.draftData.descriptionAndPhotos.youTubeVideoUrl
        );
        if (videoKey.length > 0) {
            this.draftData.descriptionAndPhotos.youTubeVideoKey = videoKey;
            this.updateState();
        } else {
            this.motorsDescriptionAndPhotosVideoSectionForm?.form?.controls?.youtubeVideo.setErrors(
                { incorrect: true }
            );
            this.previewYouTubeVideoUrl = false;
            this.hasError = true;
        }
        this.isProcessing = false;
    }

    onVideoButtonClick(): void {
        this.hasError = false;
        if (this.buttonTitle === this.addVideoText) {
            this.isProcessing = true;
            this.validateVideo();
        } else if (this.buttonTitle === this.removeVideoText) {
            this.draftData.descriptionAndPhotos.youTubeVideoKey = null;
            this.draftData.descriptionAndPhotos.youTubeVideoUrl = null;
            this.safeYouTubeVideoUrl = null;
            this.originalYouTubeVideoKey = null;
            this.previewYouTubeVideoUrl = false;
            this.updateState();
        }
        this._changeDetectorRef.markForCheck();
    }

    onInputChange(value: string) {
        if (isNullOrWhiteSpace(value)) {
            this.hasError = false;
        }
    }

    updateState() {
        this.motorsDescriptionAndPhotosVideoSectionForm.form.markAllAsTouched();
        this.motorsDescriptionAndPhotosVideoSectionForm.form.markAsDirty();
        this.publish();
    }
}
